import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { StatusBadge } from "@/components/StatusBadge";
import { CalendarDays, Clock, Video, Phone, MapPin, Zap } from "lucide-react";

const MEETING_ICON = { "In Person": MapPin, Phone: Phone, Video: Video };
const todayStr = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

export function AppointmentsPanel({ clientId, leadId, title = "Appointments" }) {
  const [appts, setAppts] = useState(null);
  useEffect(() => {
    const qs = clientId ? `client_id=${clientId}` : leadId ? `lead_id=${leadId}` : "";
    if (!qs) return;
    api.get(`/appointments?${qs}`).then((r) => setAppts(r.data)).catch(() => setAppts([]));
  }, [clientId, leadId]);

  const Row = (a) => {
    const MIcon = MEETING_ICON[a.meeting_type] || Video;
    return (
      <div key={a.id} data-testid={`profile-appt-${a.id}`} className="flex items-center gap-3 py-2 border-b border-slate-100 last:border-0">
        <div className="h-9 w-9 rounded-lg bg-primary/10 flex items-center justify-center shrink-0"><CalendarDays className="h-4 w-4 text-primary" /></div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-semibold text-slate-800">{a.type}</span>
            {a.is_emergency && <Zap className="h-3 w-3 text-primary" />}
            <StatusBadge status={a.status} />
          </div>
          <div className="text-[11px] text-slate-500 flex flex-wrap items-center gap-x-2 gap-y-0.5 mt-0.5">
            <span className="flex items-center gap-1"><Clock className="h-3 w-3" />{a.date} · {a.start_time}{a.end_time ? `–${a.end_time}` : ""}</span>
            <span className="flex items-center gap-1"><MIcon className="h-3 w-3" />{a.meeting_type}</span>
            {a.staff_name && <span>· {a.staff_name}</span>}
          </div>
        </div>
      </div>
    );
  };

  if (appts === null) return <div className="text-xs text-slate-400" data-testid="appts-panel-loading">Loading appointments…</div>;
  const sorted = [...appts].sort((a, b) => (b.date + b.start_time).localeCompare(a.date + a.start_time));
  const isUpcoming = (a) => a.date >= todayStr() && !["Cancelled", "Completed"].includes(a.status);
  const upcoming = sorted.filter(isUpcoming).reverse();
  const past = sorted.filter((a) => !isUpcoming(a));

  return (
    <div data-testid="appointments-panel">
      <div className="flex items-center gap-2 mb-3"><CalendarDays className="h-4 w-4 text-primary" /><h3 className="font-head font-bold text-slate-900 text-sm">{title}</h3></div>
      <div className="mb-3">
        <div className="text-[11px] font-bold uppercase tracking-wide text-slate-400 mb-1">Upcoming</div>
        {upcoming.length ? upcoming.map(Row) : <div className="text-xs text-slate-400">No upcoming appointments.</div>}
      </div>
      <div>
        <div className="text-[11px] font-bold uppercase tracking-wide text-slate-400 mb-1">Previous</div>
        {past.length ? past.map(Row) : <div className="text-xs text-slate-400">No previous appointments.</div>}
      </div>
    </div>
  );
}
